import { useState, useRef, useEffect } from 'react'
import { CardStory } from 'components/Cards/CardStory'
import { DotIndicators } from 'components/DotIndicators'
import { ChevronLeft, ChevronRight } from 'react-feather'
import { Button, IconButton, CircularProgress } from '@achieve/ascend'
import { useAppRouter } from 'hooks/useAppRouter/useAppRouter'
import { createGraduatedViewData, createPublicViewData } from './data'
import logger from '@achieve/cx-logger'
import { WALL_OF_FAME_ROUTE } from 'constants/navigation'
import { Image } from 'components/Image'
import { Typography } from 'components/Contentful'
import { Dialog } from '@achieve/ascend'
import { useTriggerEvents } from 'hooks/useTriggerEvents'
import { handleTrackAndReactEvent } from 'utils/analytics'
import { CardPremiumStory } from 'components/Cards/CardPremiumStory'

const months = Array.from({ length: 12 }, (_, i) =>
  new Date(2000, i, 1).toLocaleString('en-US', { month: 'long' })
)

export const WallOfFameStoryModalCarousel = ({
  story,
  isOpen,
  onClose,
  isGraduatedView = false,
}) => {
  const storyData = isGraduatedView ? createGraduatedViewData(story) : createPublicViewData(story)
  const [currentIndex, setCurrentIndex] = useState(0)
  const [loading, setLoading] = useState(false)
  const touchStartX = useRef(0)
  const touchEndX = useRef(0)
  const storiesLength = storyData.filter((story) => !story.ignore).length
  const { query } = useAppRouter()
  const { fireAndForget, waitForNavigation } = useTriggerEvents()
  useEffect(() => {
    if (isOpen) {
      ; (async () => {
        await fireAndForget({
          event_type: 'track',
          ...handleTrackAndReactEvent(event, {
            event_type: 'track',
            application: 'fdr-site',
            event_action: 'click',
            event_name: 'Expanded Story',
            event_datetime: Date.now().toString(),
            event_id: 'expanded_story',
          }),
        })
      })()
    }
  }, [fireAndForget, isOpen])

  const unpublishedAt = story?.unpublishedAt?.split('T')?.[0]?.split('-')

  if (!isOpen) return null

  // Carousel Navigation
  const goToNext = () => {
    if (currentIndex < storiesLength - 1) {
      setCurrentIndex((prev) => prev + 1)
    }
  }

  const goToPrev = () => {
    if (currentIndex > 0) {
      setCurrentIndex((prev) => prev - 1)
    }
  }

  const handleTouchStart = (e) => {
    touchStartX.current = e.touches[0].clientX
  }

  const handleTouchEnd = (e) => {
    touchEndX.current = e.changedTouches[0].clientX
    const deltaX = touchStartX.current - touchEndX.current
    const swipeThreshold = 50

    if (Math.abs(deltaX) > swipeThreshold) {
      if (deltaX > 0 && currentIndex < storiesLength - 1) {
        goToNext()
      } else if (deltaX < 0 && currentIndex > 0) {
        goToPrev()
      }
    }
  }
  // End Carousel Navigation

  // Button Handlers
  const handleUnpublishClick = async () => {
    setLoading(true)
    try {
      const res = await fetch(`/api/wall-of-fame/unpublish-graduate?auth=${query.auth}`, {
        method: 'POST',
      })

      await waitForNavigation({
        event_type: 'track',
        ...handleTrackAndReactEvent(event, {
          event_type: 'track',
          application: 'fdr-site',
          event_action: 'click',
          event_name: 'Unpublish Graduate',
          event_datetime: Date.now().toString(),
          event_id: 'unpublish_graduate',
        }),
      }).then(() => {
        setCurrentIndex(storyData.length - 1)
        window.location.href = WALL_OF_FAME_ROUTE
      })

      if (!res.ok) {
        throw new Error('Failed to unpublish')
      }
    } catch (err) {
      logger.error('Failed to unpublish in WallOfFameStoryModalCarousel', err)
    } finally {
      setLoading(false)
    }
  }

  const handleClose = () => {
    onClose()
    setCurrentIndex(0)
  }
  // End Button Handlers

  const handleViewOtherStories = () => {
    handleClose()
    const section = document.getElementById('targetSection')
    const scrollableDiv = document.getElementById('scrollableDiv')

    if (section) {
      section.scrollIntoView({ behavior: 'smooth', block: 'center' })
    }
    if (scrollableDiv) {
      scrollableDiv.scrollTo({ left: 0, behavior: 'smooth' })
    }
  }

  const getFooterContent = () => {
    return (
      <div className="flex flex-col items-center">
        {/* Navigation Indicators */}
        {((isGraduatedView && currentIndex < storyData.length - 2) || !isGraduatedView) && (
          <div className="flex self-center p-4 gap-3">
            <IconButton
              className={`transition ${currentIndex === 0 ? 'cursor-not-allowed text-gray-400' : 'hover:bg-neutral-75'
                }`}
              onClick={goToPrev}
              aria-label="Previous story"
              disabled={currentIndex === 0}
            >
              <ChevronLeft size={24} />
            </IconButton>
            <DotIndicators
              variant="onWhite"
              size={storiesLength}
              activeIndex={currentIndex}
              classNameIndicators="flex gap-4"
              classNameIndicatorDots="h-[6px] w-[6px] m-0 opacity-100 transition-all"
            />
            <IconButton
              onClick={goToNext}
              className={`transition ${currentIndex === storiesLength - 1
                ? 'cursor-not-allowed text-gray-400'
                : 'hover:bg-neutral-75'
                }`}
              aria-label="Next story"
              disabled={currentIndex === storiesLength - 1}
            >
              <ChevronRight size={24} />
            </IconButton>
          </div>
        )}

        {isGraduatedView && story?.isPublic && currentIndex === storyData.length - 3 && (
          <div className="flex flex-col justify-center gap-4 pb-6 w-[279px]">
            <Button className="bg-blue-600 text-white px-4 py-2" onClick={handleViewOtherStories}>
              View other stories
            </Button>
            <Button
              variant="text"
              className="text-content-accent px-4 py-2"
              onClick={() => setCurrentIndex((prev) => prev + 1)}
            >
              Unpublish this story
            </Button>
          </div>
        )}

        {/* Graduated Unpublish Actions Card */}
        {isGraduatedView && story?.isPublic && currentIndex === storyData.length - 2 && (
          <div className="flex flex-col justify-center gap-4 pb-6 w-[279px]">
            <Button
              onClick={handleUnpublishClick}
              disabled={loading}
              className={`${loading ? 'bg-neutral-100' : 'bg-blue-600'
                } text-white px-4 py-2 flex items-center justify-center gap-2`}
            >
              {loading ? <CircularProgress size={20} /> : 'Yes, unpublish my story'}
            </Button>
            <Button
              variant="text"
              className="text-content-accent px-4 py-2"
              disabled={loading}
              onClick={handleViewOtherStories}
            >
              Keep my story published
            </Button>
          </div>
        )}

        {/* Graduated Last Card */}
        {isGraduatedView && currentIndex === storyData.length - 1 && (
          <div className="flex flex-col justify-center gap-4 pb-6 w-[279px]">
            <Button className="bg-blue-600 text-white px-4 py-2" onClick={handleViewOtherStories}>
              View other stories
            </Button>
          </div>
        )}
      </div>
    )
  }

  return (
    <Dialog open={true} onClose={handleClose}>
      <div className="fixed inset-0 z-50 flex items-center justify-center">
        <div
          className="absolute inset-0 bg-[#27313EBF] bg-opacity-75 backdrop-blur-sm"
          onClick={handleClose}
        />
        <div
          className="relative flex flex-col items-center"
          onTouchStart={handleTouchStart}
          onTouchEnd={handleTouchEnd}
        >
          {isGraduatedView && story?.isPublic == false ? (
            <div className="md:w-[672px] w-[327px] relative rounded-md bg-white">
              <div className="relative overflow-hidden md:h-[522px] h-[460px] flex flex-col gap-8 md:px-8 px-4 md:py-16 py-8">
                <div
                  onClick={onClose}
                  className="absolute cursor-pointer md:top-6 top-2 top:12 right-4"
                >
                  <Image
                    src="/multi-site-assets/close.svg"
                    alt="Close button"
                    width={18}
                    height={18}
                    priority={false}
                    aria-hidden={true}
                    data-testid="card-story-x-icon"
                  />
                </div>

                <div class="flex flex-col items-center justify-center bg-white px-4 text-center">
                  <Image
                    src="/multi-site-assets/DollarSearch.svg"
                    alt="Dollar Search"
                    width={88}
                    height={100}
                    priority={false}
                    aria-hidden={true}
                    data-testid="card-dollar-search-icon"
                    className="md:mb-9 mb-4"
                  />

                  <Typography
                    className="font-semibold text-gray-900 md:mb-5 mb-2"
                    variant="headingLg"
                    mobileVariant="headingMd"
                    responsive={true}
                    content="Your story is unavailable"
                  />

                  <Typography
                    className="text-gray-600 max-w-xl md:mb-8 mb-4 break-words whitespace-normal"
                    variant="bodyMd"
                    content={`You previously unpublished your story on ${months[parseInt(unpublishedAt?.[1]) - 1]
                      } ${unpublishedAt?.[2]}, ${unpublishedAt?.[0]}, so its no longer
                visible. Find out how others kicked debt to the curb by viewing the stories on this
                page.`}
                  />

                  <Button
                    variant="contained"
                    className="text-white px-4 py-2 md:min-w-64 min-w-56 "
                    onClick={onClose}
                  >
                    View other stories
                  </Button>
                </div>
              </div>
            </div>
          ) : isGraduatedView && story?.isPublic && !story?.premiumDescription ? (
            <CardPremiumStory
              {...storyData[currentIndex]}
              {...story}
              onClose={handleClose}
              footerContent={getFooterContent()}
              index={currentIndex}
            />
          ) : (
            <CardStory
              {...storyData[currentIndex]}
              onClose={handleClose}
              footerContent={getFooterContent()}
            />
          )}
        </div>
      </div>


    </Dialog>
  )
}
